import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pencil, Eye, Ear, Hand, BookOpen, Download, Share2, Copy, RefreshCw, X, LineChart, Package } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STYLE_META = {
  visual: { label: "Visual", icon: Eye, bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200" },
  auditory: { label: "Auditory", icon: Ear, bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200" },
  kinesthetic: { label: "Kinesthetic", icon: Hand, bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200" },
  reading: { label: "Reading/Writing", icon: BookOpen, bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
};

const GRADES = ["Pre-K", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const emptyForm = { name: "", grade: "K", learning_style: "visual", interests: "", subscription_tier: "basic" };

export default function Students() {
  const [students, setStudents] = useState([]);
  const [studentDetails, setStudentDetails] = useState({}); // sid -> {attendance, extras}
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStudent, setShareStudent] = useState(null);
  const [share, setShare] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/students");
    setStudents(data);
    // Fetch attendance + proficiency summary for each student
    const details = {};
    await Promise.all(
      data.map(async (s) => {
        try {
          const [att, prof] = await Promise.all([
            api.get(`/students/${s.id}/attendance`).then((r) => r.data).catch(() => null),
            api.get(`/students/${s.id}`).then((r) => r.data).catch(() => null),
          ]);
          details[s.id] = { attendance: att, extras: prof };
        } catch {}
      })
    );
    setStudentDetails(details);
  };

  useEffect(() => {
    load().catch(() => {});
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      grade: s.grade,
      learning_style: s.learning_style,
      interests: s.interests || "",
      subscription_tier: s.subscription_tier || "basic",
    });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/students/${editing.id}`, form);
        toast.success("Student updated");
      } else {
        await api.post("/students", form);
        toast.success("Student added");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? This deletes their lessons too.`)) return;
    try {
      await api.delete(`/students/${s.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadPortfolio = async (s) => {
    try {
      const res = await api.get(`/portfolio/${s.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `portfolio-${s.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Portfolio downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const printPack = async (s) => {
    try {
      const res = await api.post(
        `/worksheets/print-pack`,
        { student_id: s.id, with_answers: false },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${s.name.replace(/\s+/g, "_")}-print-pack.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Print pack downloaded for ${s.name}`);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        toast.error("No worksheets yet — generate some from the Schedule tab first");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    }
  };

  const openShare = async (s) => {
    setShareStudent(s);
    setShare(null);
    setShareOpen(true);
    try {
      const { data } = await api.get(`/portfolio/${s.id}/share`);
      setShare(data.share);
    } catch {
      setShare(null);
    }
  };

  const createOrRotateShare = async () => {
    if (!shareStudent) return;
    setShareBusy(true);
    try {
      const { data } = await api.post(`/portfolio/${shareStudent.id}/share`);
      setShare(data.share);
      toast.success("Share link ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setShareBusy(false);
    }
  };

  const revokeShare = async () => {
    if (!shareStudent) return;
    setShareBusy(true);
    try {
      await api.delete(`/portfolio/${shareStudent.id}/share`);
      setShare(null);
      toast.success("Link revoked");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setShareBusy(false);
    }
  };

  const copyShareUrl = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Roster</div>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Students</h1>
          <p className="mt-2 text-stone-600 max-w-2xl">
            <span className="font-medium text-stone-900">Click any student</span> to update proficiencies, log WRAT-5 assessments, track attendance, review lessons, and open their evaluation packet.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAdd} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-student-btn">
              <Plus className="w-4 h-4 mr-2" /> Add student
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-2xl" data-testid="student-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit student" : "Add student"}</DialogTitle>
              <DialogDescription>Add a student profile with a learning-style tag so recommendations and reports adapt to them.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 rounded-xl h-11"
                  data-testid="student-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Grade</Label>
                  <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                    <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="student-grade-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GRADES.map((g) => (
                        <SelectItem key={g} value={g}>Grade {g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Learning style</Label>
                  <Select value={form.learning_style} onValueChange={(v) => setForm({ ...form, learning_style: v })}>
                    <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="student-style-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STYLE_META).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Interests (comma separated)</Label>
                <Textarea
                  value={form.interests}
                  onChange={(e) => setForm({ ...form, interests: e.target.value })}
                  className="mt-2 rounded-xl min-h-[80px]"
                  placeholder="dinosaurs, LEGO, marine biology"
                  data-testid="student-interests-input"
                />
              </div>
              <div>
                <Label>Subscription tier</Label>
                <Select value={form.subscription_tier} onValueChange={(v) => setForm({ ...form, subscription_tier: v })}>
                  <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="student-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic — $2/mo</SelectItem>
                    <SelectItem value="plus">Plus — $5/mo</SelectItem>
                    <SelectItem value="premium">Premium — $9/mo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button
                onClick={save}
                disabled={saving || !form.name}
                className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="student-save-btn"
              >
                {saving ? "Saving…" : editing ? "Update" : "Add"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {students.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center" data-testid="students-empty">
          <div className="font-display text-xl font-semibold">No students yet</div>
          <p className="mt-2 text-stone-500">Add your first student to unlock scheduling and personalized recommendations.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {students.map((s) => {
            const meta = STYLE_META[s.learning_style] || STYLE_META.visual;
            const Icon = meta.icon;
            return (
              <div key={s.id} className="bg-white border-2 border-stone-200 hover:border-amber-400 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all cursor-pointer group" data-testid={`student-card-${s.id}`} onClick={() => window.location.assign(`/dashboard/students/${s.id}`)}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-xl font-semibold group-hover:text-amber-600 transition-colors truncate">{s.name}</div>
                    <div className="text-sm text-stone-500 mt-0.5">Grade {s.grade}</div>
                    {typeof s.trial_days_remaining === "number" && s.subscription_status === "trial" && (
                      <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 text-[11px] font-semibold" data-testid={`card-trial-badge-${s.id}`}>
                        Trial · {s.trial_days_remaining}d left
                      </div>
                    )}
                    {s.subscription_status === "trial_expired" && (
                      <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`card-trial-expired-${s.id}`}>
                        Trial ended
                      </div>
                    )}
                    {s.subscription_status === "paused" && typeof s.pause_days_remaining === "number" && (
                      <div className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${s.pause_days_remaining <= 20 ? "bg-red-50 border-red-200 text-red-700" : "bg-stone-100 border-stone-300 text-stone-700"}`} data-testid={`card-paused-badge-${s.id}`}>
                        Paused · {s.pause_days_remaining}d left
                      </div>
                    )}
                    {s.subscription_status === "pause_expired" && (
                      <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-300 text-red-700 px-2 py-0.5 text-[11px] font-semibold" data-testid={`card-atrisk-badge-${s.id}`}>
                        At risk — resume soon
                      </div>
                    )}
                  </div>
                  <div className={`inline-flex items-center gap-1.5 rounded-full ${meta.bg} ${meta.text} px-3 py-1 text-xs font-semibold ring-1 ${meta.ring}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                  </div>
                </div>

                {/* Attendance mini-progress */}
                {studentDetails[s.id]?.attendance && (
                  <div className="mt-4 grid grid-cols-2 gap-3" data-testid={`card-attendance-${s.id}`}>
                    <div className="rounded-xl bg-stone-50 p-2 text-center">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Days</div>
                      <div className="font-display text-lg font-bold">{studentDetails[s.id].attendance.days_logged}<span className="text-xs font-normal text-stone-400"> / 180</span></div>
                      <div className="h-1 rounded-full bg-stone-200 mt-1 overflow-hidden">
                        <div className="h-full bg-amber-500" style={{ width: `${studentDetails[s.id].attendance.days_pct}%` }} />
                      </div>
                    </div>
                    <div className="rounded-xl bg-stone-50 p-2 text-center">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Hours</div>
                      <div className="font-display text-lg font-bold">{studentDetails[s.id].attendance.hours_logged}<span className="text-xs font-normal text-stone-400"> / 720</span></div>
                      <div className="h-1 rounded-full bg-stone-200 mt-1 overflow-hidden">
                        <div className="h-full bg-emerald-500" style={{ width: `${studentDetails[s.id].attendance.hours_pct}%` }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Strengths / weaknesses */}
                {studentDetails[s.id]?.extras?.strengths_weaknesses && (
                  <div className="mt-3 space-y-1.5 text-xs" data-testid={`card-sw-${s.id}`}>
                    {studentDetails[s.id].extras.strengths_weaknesses.strengths.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-emerald-700 font-semibold uppercase tracking-[0.15em] text-[10px]">Strong</span>
                        {studentDetails[s.id].extras.strengths_weaknesses.strengths.slice(0, 3).map((x) => (
                          <span key={x} className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5">{x}</span>
                        ))}
                      </div>
                    )}
                    {studentDetails[s.id].extras.strengths_weaknesses.weaknesses.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-red-700 font-semibold uppercase tracking-[0.15em] text-[10px]">Focus</span>
                        {studentDetails[s.id].extras.strengths_weaknesses.weaknesses.slice(0, 3).map((x) => (
                          <span key={x} className="rounded-full bg-red-50 border border-red-200 text-red-700 px-2 py-0.5">{x}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                  <div className="text-sm font-medium text-amber-600 group-hover:text-amber-700 inline-flex items-center gap-1">
                    Open profile →
                  </div>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="rounded-full text-stone-500 hover:text-stone-800" data-testid={`edit-student-${s.id}`} title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => downloadPortfolio(s)} className="rounded-full text-emerald-700" data-testid={`portfolio-student-${s.id}`} title="Portfolio PDF">
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => printPack(s)} className="rounded-full text-indigo-700" data-testid={`print-pack-${s.id}`} title="Worksheet print pack">
                      <Package className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => openShare(s)} className="rounded-full text-amber-700" data-testid={`share-student-${s.id}`} title="Share">
                      <Share2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(s)} className="rounded-full text-red-600" data-testid={`delete-student-${s.id}`} title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Share Portfolio Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Share {shareStudent?.name}'s portfolio</DialogTitle>
            <DialogDescription>
              Anyone with this link can view a read-only PDF of the portfolio (subject log, lessons, resource picks). Ideal for district evaluators.
            </DialogDescription>
          </DialogHeader>

          {share ? (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-stone-500">Public link</Label>
                <div className="mt-2 flex gap-2">
                  <Input value={share.url} readOnly className="rounded-xl h-11 font-mono text-xs" data-testid="share-url-input" onFocus={(e) => e.target.select()} />
                  <Button onClick={copyShareUrl} className="rounded-xl bg-stone-900 hover:bg-stone-800 text-white shrink-0" data-testid="share-copy-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-2 text-xs text-stone-500">
                  Views: <span className="font-medium text-stone-700">{share.views ?? 0}</span>
                </div>
              </div>
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                🔒 Anyone with this link can view (but not edit) the portfolio. Revoke it anytime — a new link can be generated.
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={createOrRotateShare} disabled={shareBusy} className="rounded-full border-2 border-stone-200" data-testid="share-rotate-btn">
                  <RefreshCw className="w-4 h-4 mr-2" /> Rotate link
                </Button>
                <Button variant="ghost" onClick={revokeShare} disabled={shareBusy} className="rounded-full text-red-600 hover:bg-red-50" data-testid="share-revoke-btn">
                  <X className="w-4 h-4 mr-2" /> Revoke
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl bg-stone-50 border border-stone-200 p-6 text-center">
                <Share2 className="w-8 h-8 text-stone-400 mx-auto" />
                <div className="mt-3 font-medium">No active share link</div>
                <div className="mt-1 text-sm text-stone-500">Create one below — you can revoke it anytime.</div>
              </div>
              <Button onClick={createOrRotateShare} disabled={shareBusy} className="w-full rounded-full bg-amber-500 hover:bg-amber-600 text-white h-11" data-testid="share-create-btn">
                {shareBusy ? "Creating…" : "Create shareable link"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
